import { webLog } from '../../../shared/api/grpc/webLogger';

function createTraceKey(value: string): string {
  let hash = 2166136261;

  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return `${value.length}:${(hash >>> 0).toString(36)}`;
}

export function toTraceKey(value: string | null | undefined): string | null {
  return value ? createTraceKey(value) : null;
}

type SerializableTraceValue =
  | null
  | string
  | number
  | boolean
  | SerializableTraceValue[]
  | { [key: string]: SerializableTraceValue };

interface MappingTraceEntry {
  sequence: number;
  timestamp: string;
  event: string;
  payload: SerializableTraceValue;
}

type MappingTraceWindow = Window & {
  __PIPEEX_MAPPING_FLOW_TRACE__?: MappingTraceEntry[];
  __PIPEEX_MAPPING_TRACE__?: boolean;
  dumpMappingFlowTrace?: () => MappingTraceEntry[];
  copyMappingFlowTrace?: () => string;
};

const MaxTraceEntries = 500;
const MappingTraceFlagParam = 'mappingTrace';
const E2eMappingTraceFlagParam = 'e2eMappingTrace';
const MappingTraceStorageFlag = 'pipeex:mappingTrace';
const MappingTraceEnvFlag = process.env.REACT_APP_PIPEEX_MAPPING_TRACE;

let traceSequence = 0;

function isSensitiveProjectIdentityField(key: string): boolean {
  const normalizedKey = key.toLowerCase();
  return normalizedKey.endsWith('projectkey')
    || normalizedKey.endsWith('documentpath')
    || normalizedKey.endsWith('documentname');
}

function getTraceWindow(): MappingTraceWindow | null {
  if (typeof window === 'undefined') {
    return null;
  }

  return window as MappingTraceWindow;
}

function isEnabledFlag(value: string | null | undefined): boolean {
  return value === '1' || value === 'true';
}

function readPersistedMappingTraceFlag(): string | null {
  try {
    return getTraceWindow()?.localStorage?.getItem(MappingTraceStorageFlag) ?? null;
  } catch {
    return null;
  }
}

export function isMappingTraceEnabled(
  search = getTraceWindow()?.location?.search ?? '',
  nodeEnv = process.env.NODE_ENV,
  envFlag = MappingTraceEnvFlag,
  persistedFlag = readPersistedMappingTraceFlag(),
  windowFlag = getTraceWindow()?.__PIPEEX_MAPPING_TRACE__
): boolean {
  if (nodeEnv === 'test') {
    return false;
  }

  const params = new URLSearchParams(search);
  const explicitTraceEnabled =
    isEnabledFlag(envFlag) ||
    isEnabledFlag(params.get(MappingTraceFlagParam)) ||
    isEnabledFlag(persistedFlag) ||
    windowFlag === true;
  const e2eTraceEnabled =
    nodeEnv === 'development' &&
    params.get('e2e') === '1' &&
    isEnabledFlag(params.get(E2eMappingTraceFlagParam));

  return explicitTraceEnabled || e2eTraceEnabled;
}

function serializeUnknownTraceValue(
  value: unknown,
  seen: WeakSet<object>
): SerializableTraceValue {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'bigint') {
    return value.toString();
  }

  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      stack: value.stack ?? null,
    };
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (Array.isArray(value)) {
    return value.map((item) => serializeUnknownTraceValue(item, seen));
  }

  if (typeof value === 'object') {
    if (seen.has(value)) {
      return '[Circular]';
    }

    seen.add(value);

    const result: { [key: string]: SerializableTraceValue } = {};
    Object.entries(value as Record<string, unknown>).forEach(([key, entryValue]) => {
      result[key] = isSensitiveProjectIdentityField(key) && typeof entryValue === 'string'
        ? createTraceKey(entryValue)
        : serializeUnknownTraceValue(entryValue, seen);
    });

    seen.delete(value);
    return result;
  }

  if (typeof value === 'function') {
    return '[Function]';
  }

  return String(value);
}

export function normalizeMappingTracePayload(
  payload?: Record<string, unknown>
): SerializableTraceValue {
  return serializeUnknownTraceValue(payload ?? {}, new WeakSet<object>());
}

export function formatMappingTracePayload(payload?: Record<string, unknown>): string {
  return JSON.stringify(normalizeMappingTracePayload(payload));
}

function rememberMappingTrace(entry: MappingTraceEntry): void {
  const traceWindow = getTraceWindow();
  if (!traceWindow) {
    return;
  }

  const buffer = traceWindow.__PIPEEX_MAPPING_FLOW_TRACE__ ?? [];
  buffer.push(entry);
  if (buffer.length > MaxTraceEntries) {
    buffer.splice(0, buffer.length - MaxTraceEntries);
  }

  traceWindow.__PIPEEX_MAPPING_FLOW_TRACE__ = buffer;
  traceWindow.dumpMappingFlowTrace = () => [...(traceWindow.__PIPEEX_MAPPING_FLOW_TRACE__ ?? [])];
  traceWindow.copyMappingFlowTrace = () => {
    const text = JSON.stringify(traceWindow.__PIPEEX_MAPPING_FLOW_TRACE__ ?? [], null, 2);
    void traceWindow.navigator?.clipboard?.writeText(text);
    return text;
  };
}

export function traceMappingFlow(event: string, payload?: Record<string, unknown>): void {
  if (!isMappingTraceEnabled()) {
    return;
  }

  const normalizedPayload = normalizeMappingTracePayload(payload);
  const entry: MappingTraceEntry = {
    sequence: traceSequence + 1,
    timestamp: new Date().toISOString(),
    event,
    payload: normalizedPayload,
  };

  traceSequence = entry.sequence;
  rememberMappingTrace(entry);

  const message = `[trace#${entry.sequence}] ${event} ${JSON.stringify(normalizedPayload)}`;
  webLog.info('mappingFlow', message);
  console.info(`[MappingFlow]${message}`, normalizedPayload);
}

export function warnMappingFlow(event: string, payload?: Record<string, unknown>): void {
  const normalizedPayload = normalizeMappingTracePayload(payload);
  const entry: MappingTraceEntry = {
    sequence: traceSequence + 1,
    timestamp: new Date().toISOString(),
    event,
    payload: normalizedPayload,
  };

  traceSequence = entry.sequence;
  rememberMappingTrace(entry);
  webLog.warn('mappingFlow', `[trace#${entry.sequence}] ${event} ${JSON.stringify(normalizedPayload)}`);
}

export function infoMappingFlow(event: string, payload?: Record<string, unknown>): void {
  const normalizedPayload = normalizeMappingTracePayload(payload);
  const entry: MappingTraceEntry = {
    sequence: traceSequence + 1,
    timestamp: new Date().toISOString(),
    event,
    payload: normalizedPayload,
  };

  traceSequence = entry.sequence;
  rememberMappingTrace(entry);
  webLog.info('mappingFlow', `[trace#${entry.sequence}] ${event} ${JSON.stringify(normalizedPayload)}`);
}
